import { Navigate } from 'react-router-dom';
import { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { UserRole } from '../types';
import AccessDenied from './AccessDenied';

interface PermissionRouteProps {
  children: ReactNode;
  permission?: string;
  anyOf?: string[];
  roles?: UserRole[];
  tenantOnly?: boolean;
}

const PermissionRoute = ({ children, permission, anyOf, roles, tenantOnly = false }: PermissionRouteProps) => {
  const { user, tenant, isLoading } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If user is not authenticated, redirect to login (handled by ProtectedRoute)
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (tenantOnly && (user.is_host || !tenant?.id)) {
    return <AccessDenied />;
  }

  // If a specific set of roles is required and the user's role is not among them
  if (roles && !roles.includes(user.role)) {
    // Client users attempting a staff-only route go to the catalog
    if (user.role === 'cliente') {
      return <Navigate to="/catalog" replace />;
    }
    // Staff users attempting a client-only route (or any other mismatch) go home
    return <Navigate to="/" replace />;
  }

  // Host users always bypass permission checks (handled internally by hasPermission)
  if (permission && !hasPermission(permission)) {
    return <AccessDenied />;
  }

  if (anyOf && !hasAnyPermission(anyOf)) {
    return <AccessDenied />;
  }

  return <>{children}</>;
};

export default PermissionRoute;
